import * as React from "react"
import { ChevronDownIcon } from "@heroicons/react/24/outline"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"
import { useMediaQuery } from "@/lib/hooks/useMediaQuery"

/**
 * Story 24.3 — SegmentedControl (DESIGN.md › Components › SegmentedControl).
 * Roles only.
 *
 * - A `bg-hover` pill track with 2–4 equal segments, at least 44px tall.
 *   Selected: a `bg-surface` thumb with a 1px `border-ink-muted` edge (the
 *   non-colour cue) and `type-label-selected text-ink`. Idle:
 *   `type-label text-ink-muted-on-wash` (muted on `hover` fails contrast).
 * - `role="radiogroup"` of `role="radio"` buttons with a roving tabindex:
 *   arrow keys move and select (wrapping), Tab leaves the group.
 * - Below 400px, or when a segment's label would not fit, it renders as a
 *   native `<select>` with a visible label (`selectLabel`, "Show" by default;
 *   Story 25.8's Theme control passes "Theme"), drawn as a secondary Button
 *   with a chevron. It never scrolls horizontally.
 */

const NARROW_QUERY = "(max-width: 399.98px)"

type SegmentedOption = { value: string; label: string }

type SegmentedControlProps = {
  /** The group's accessible name. */
  label: string
  /** 2–4 options. */
  options: readonly SegmentedOption[]
  value: string
  onChange: (value: string) => void
  /** Visible label of the narrow / overflow `<select>` fallback. Defaults to "Show". */
  selectLabel?: string
  className?: string
}

function isOverflowing(track: HTMLElement): boolean {
  if (track.scrollWidth > track.clientWidth) return true
  return Array.from(track.children).some(
    (child) => (child as HTMLElement).scrollWidth > (child as HTMLElement).clientWidth
  )
}

function SegmentedControl({
  label,
  options,
  value,
  onChange,
  selectLabel = "Show",
  className,
}: SegmentedControlProps) {
  const narrow = useMediaQuery(NARROW_QUERY)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const trackRef = React.useRef<HTMLDivElement>(null)
  const buttonRefs = React.useRef<Array<HTMLButtonElement | null>>([])
  const selectId = React.useId()
  // The wrapper width at which the segments overflowed; the radiogroup is
  // tried again only once the wrapper grows past it.
  const [overflowWidth, setOverflowWidth] = React.useState<number | null>(null)
  const useSelect = narrow || overflowWidth !== null
  const labelsKey = options.map((option) => option.label).join("\u0000")

  React.useLayoutEffect(() => {
    if (useSelect) return
    const track = trackRef.current
    if (!track) return
    const measure = () => {
      if (isOverflowing(track)) setOverflowWidth(wrapperRef.current?.clientWidth ?? 0)
    }
    measure()
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(measure)
    observer.observe(track)
    return () => observer.disconnect()
  }, [useSelect, labelsKey])

  React.useEffect(() => {
    if (overflowWidth === null || typeof ResizeObserver === "undefined") return
    const wrapper = wrapperRef.current
    if (!wrapper) return
    const observer = new ResizeObserver(() => {
      if (wrapper.clientWidth > overflowWidth) setOverflowWidth(null)
    })
    observer.observe(wrapper)
    return () => observer.disconnect()
  }, [overflowWidth])

  const selectedIndex = options.findIndex((option) => option.value === value)
  const focusIndex = selectedIndex >= 0 ? selectedIndex : 0

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let step = 0
    if (event.key === "ArrowRight" || event.key === "ArrowDown") step = 1
    else if (event.key === "ArrowLeft" || event.key === "ArrowUp") step = -1
    else return
    event.preventDefault()
    const next = (index + step + options.length) % options.length
    buttonRefs.current[next]?.focus()
    onChange(options[next].value)
  }

  return (
    <div ref={wrapperRef} data-slot="segmented-control" className={cn("min-w-0", className)}>
      {useSelect ? (
        <div className="flex items-center gap-2">
          <label htmlFor={selectId} className="type-label text-ink">
            {selectLabel}
          </label>
          <div className="relative min-w-0 flex-1">
            <select
              id={selectId}
              data-slot="segmented-control-select"
              value={value}
              onChange={(event) => onChange(event.target.value)}
              className={cn(
                buttonVariants({ variant: "secondary" }),
                "w-full appearance-none justify-start pr-10 text-left"
              )}
            >
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 right-3 size-5 -translate-y-1/2 text-ink-muted"
            />
          </div>
        </div>
      ) : (
        <div
          ref={trackRef}
          role="radiogroup"
          aria-label={label}
          className="inline-grid max-w-full gap-1 rounded-full bg-hover p-1"
          style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
        >
          {options.map((option, index) => {
            const selected = index === selectedIndex
            return (
              <button
                key={option.value}
                ref={(el) => {
                  buttonRefs.current[index] = el
                }}
                type="button"
                role="radio"
                aria-checked={selected}
                tabIndex={index === focusIndex ? 0 : -1}
                data-state={selected ? "on" : "off"}
                onClick={() => onChange(option.value)}
                onKeyDown={(event) => onKeyDown(event, index)}
                className={cn(
                  "min-h-11 overflow-hidden rounded-full border px-4 whitespace-nowrap transition-colors motion-reduce:transition-none focus-ring",
                  selected
                    ? "border-ink-muted bg-surface type-label-selected text-ink"
                    : "border-transparent type-label text-ink-muted-on-wash"
                )}
              >
                {option.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export { SegmentedControl }
export type { SegmentedControlProps, SegmentedOption }
